/* import __COLOCATED_TEMPLATE__ from './lsg-countdown-timer-text.hbs'; */
import { later } from '@ember/runloop';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@ember/component';

/**
 * This a generic countdown timer that will show arbitrary text or a countdown timer if the time
 * is passed the given threshold
 */
export default Component.extend({
  /**
   * @inheritDoc
   */
  tagName: 'span',
  classNames: ['LSG-c-countdown-timer-text'],

  /**
   * Register services
   */
  moment: service(),

  /*********************************
   * REQUIRED PARAMS
   *********************************/

  /**
   * This is the date to countdown from
   *
   * @param {Date}
   */
  dateToCountdownFrom: new Date(),

  /*********************************
   * OPTIONAL PARAMS
   **********************************/

  /**
   * This will be called exactly once when the countdown timer has run to less than 0.
   *
   * @param {Function}
   */
  handleCountdownExpired: () => (function() {}),

  /**
   * @param {Boolean}       True if we will show the clock timer icon in addition to the text.
   */
  isShowTimerIcon: true,

  /**
   * The threshold before we will show the countdown timer.  We will show relative text before this.
   *
   * e.g. Expires in 5 days => Expires in 23:23:23 (After threshold is hit, we will show countdown timer)
   *
   * @param {Number}     in ms
   */
  thresholdBeforeShowingTimer: 86400000, // day in ms

  /**
   * @param {String}      A tooltip to show.  NOTE -- Will only matter when isShowTimerIcon is set to true.
   */
  tooltip: '',

  /**
   * This is the prefix before the relative string.  This is not to smart as of now, so you must
   * form a string like 'Expires in' or 'Updates in' where you are assuming a relative string
   * or timer is coming at the end.  e.g. 'Expires in{ 5 days}'.  This is not localizable for now.
   *
   * @param {String}
   */
  relativeTextToShowBeforeCountdown: 'Expires in',

  /*********************************
   * LIFECYCLE FUNCTIONS
   **********************************/

  /**
   * @inheritDoc
   */
  didInsertElement() {
    this._updateCountdownTimer();
  },

  /*********************************
   * PRIVATE
   **********************************/

  /**
   * This will be continually updated by our countdown timer and will have the state of the world
   * for the countdown timer we are showing.
   *
   * @type {Date}
   */
  _countdownTimestamp: new Date(),

  /**
   * @type {Boolean}      True if we have fired the event saying the countdown has expired.
   */
  _hasFiredCountdownExpired: false,

  /**
   * @type {Boolean}      True when the timer has expired.
   */
  _isExpired: computed('_timeFromNow', function() {
    return this.get('_timeFromNow') < 0;
  }),

  /**
   * @type {Boolean}      True if we are showing the countdown timer or false if we are showing the relative time.
   */
  _isShowingTimer: computed(
  '_timeFromNow',
  'thresholdBeforeShowingTimer',
  function() {
    let timestampFromCurrentTime = this.get('_timeFromNow');
    return timestampFromCurrentTime > 0 && timestampFromCurrentTime <= this.get('thresholdBeforeShowingTimer');
  }),

  /**
   * When we are not showing the timer, we will show this relative text.
   *
   * e.g. 'Expires in 6 days'
   *
   * @type {String}
   */
  _relativeTextToShow: computed(
  '_timeFromNow',
  'dateToCountdownFrom',
  'relativeTextToShowBeforeCountdown',
  function() {
    if (this.get('_timeFromNow') < 0) {
      return '';
    }

    let daysLeft = this.get('moment').moment(this.get('dateToCountdownFrom'));
    return `${this.get('relativeTextToShowBeforeCountdown')} ${daysLeft.toNow(true)}`;
  }),

  /**
   * @type {Number}     The time from now to the date we want to count down from.
   */
  get _timeFromNow() {
    return this.get('dateToCountdownFrom').getTime() - new Date().getTime();
  },

  /**
   * Update our _countdownTimestamp with the latest time.  Will recursively call itself until the component
   * destroys or the timer goes below 0.
   */
  _updateCountdownTimer() {
    if (this.get('isDestroying') || this.get('isDestroyed')) {
      // when the component goes away, stop this loop.
      return;
    }

    let timestampFromCurrentTime = this.get('_timeFromNow'),
      timestampFromCurrentTimeMoment = this.get('moment').moment(timestampFromCurrentTime),
      utcOffsetInMS = timestampFromCurrentTimeMoment.utcOffset() * 1000 * 60,
      countdownTimeToSetTimestamp = timestampFromCurrentTime - utcOffsetInMS;

    if (timestampFromCurrentTime < 0) {
      this.set('_isShowingTimer', false);

      if (!this.get('_hasFiredCountdownExpired')) {
        this.set('_hasFiredCountdownExpired', true);
        this.get('handleCountdownExpired')();
      }

      return;
    }

    this.set('_countdownTimestamp', this.get('moment').moment(countdownTimeToSetTimestamp).toDate());
    if (timestampFromCurrentTime > 0 && timestampFromCurrentTime <= this.get('thresholdBeforeShowingTimer')) {
      later(this, function() {
        this._updateCountdownTimer();
      }, 1000);
    }
  }
});
